import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

// Define the types for settings
export type Language = 'en' | 'pt' | 'fr';
export type FontSize = 'small' | 'medium' | 'large' | 'extra-large';
export type FontFamily = 'sans-serif' | 'serif' | 'opendyslexic';
export type ContrastMode =
  | 'standard'
  | 'high-contrast-bw'
  | 'dark-mode'
  | 'light-mode';

// Define the shape of the context state
interface SettingsState {
  language: Language;
  fontSize: FontSize;
  fontFamily: FontFamily;
  contrastMode: ContrastMode;
  setLanguage: (language: Language) => void;
  setFontSize: (size: FontSize) => void;
  setFontFamily: (family: FontFamily) => void;
  setContrastMode: (mode: ContrastMode) => void;
}

// Default settings
const defaultSettings: Omit<
  SettingsState,
  'setLanguage' | 'setFontSize' | 'setFontFamily' | 'setContrastMode'
> = {
  language: 'en',
  fontSize: 'medium',
  fontFamily: 'sans-serif',
  contrastMode: 'standard',
};

// Create the context
const SettingsContext = createContext<SettingsState | undefined>(undefined);

// Helper function to safely get item from localStorage
const getLocalStorageItem = <T,>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    console.error(`Error reading localStorage key “${key}”:`, error);
    return defaultValue;
  }
};

// Helper function to safely set item in localStorage
const setLocalStorageItem = <T,>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting localStorage key “${key}”:`, error);
  }
};

// Create the provider component
interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();

  // Initialize state from localStorage or defaults
  const [language, setLanguageState] = useState<Language>(() =>
    getLocalStorageItem<Language>('settings.language', defaultSettings.language)
  );
  const [fontSize, setFontSizeState] = useState<FontSize>(() =>
    getLocalStorageItem<FontSize>('settings.fontSize', defaultSettings.fontSize)
  );
  const [fontFamily, setFontFamilyState] = useState<FontFamily>(() =>
    getLocalStorageItem<FontFamily>(
      'settings.fontFamily',
      defaultSettings.fontFamily
    )
  );
  const [contrastMode, setContrastModeState] = useState<ContrastMode>(() =>
    getLocalStorageItem<ContrastMode>(
      'settings.contrastMode',
      defaultSettings.contrastMode
    )
  );

  // Update localStorage and i18n when language changes
  const setLanguage = useCallback(
    (lang: Language) => {
      setLanguageState(lang);
      setLocalStorageItem('settings.language', lang);
      i18n.changeLanguage(lang).catch((err: unknown) => {
        console.error('Error changing language:', err);
      });
    },
    [i18n]
  );

  // Update localStorage when font size changes
  const setFontSize = useCallback((size: FontSize) => {
    setFontSizeState(size);
    setLocalStorageItem('settings.fontSize', size);
  }, []);

  // Update localStorage when font family changes
  const setFontFamily = useCallback((family: FontFamily) => {
    setFontFamilyState(family);
    setLocalStorageItem('settings.fontFamily', family);
  }, []);

  // Update localStorage when contrast mode changes
  const setContrastMode = useCallback((mode: ContrastMode) => {
    setContrastModeState(mode);
    setLocalStorageItem('settings.contrastMode', mode);
  }, []);

  // Effect to apply settings globally (e.g., to documentElement)
  useEffect(() => {
    const root = document.documentElement;
    const fontSizes: FontSize[] = ['small', 'medium', 'large', 'extra-large'];
    const fontFamilies: FontFamily[] = ['sans-serif', 'serif', 'opendyslexic'];
    const contrastModes: ContrastMode[] = ['standard', 'high-contrast-bw', 'dark-mode', 'light-mode'];

    // Remove all possible setting classes before adding the current ones
    root.classList.remove(
      ...fontSizes.map(size => `font-size-${size}`),
      ...fontFamilies.map(family => `font-family-${family}`),
      ...contrastModes.map(mode => `contrast-${mode}`)
    );

    // Add current classes
    root.classList.add(
      `font-size-${fontSize}`,
      `font-family-${fontFamily}`,
      `contrast-${contrastMode}`
    );
    // Update lang attribute
    root.lang = language;
  }, [fontSize, fontFamily, contrastMode, language]);

  // Initial language setup
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language).catch((err: unknown) => {
        console.error('Error setting initial language:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const value = {
    language,
    fontSize,
    fontFamily,
    contrastMode,
    setLanguage,
    setFontSize,
    setFontFamily,
    setContrastMode,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use the settings context
export const useSettings = (): SettingsState => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
